/**
 * Shared Salesforce field configuration for both Settings page and Export UX.
 * This is the single source of truth for Salesforce field mappings.
 *
 * NOTE: This file is in the frontend utils directory but may be imported by
 * the backend (@edulead/services). Keep it free of frontend-specific dependencies.
 */

import { CrmFieldType, CrmMappableField } from './hubspot-field-config';

/**
 * All fields that can be mapped for CONTACT exports to Salesforce.
 * Used by:
 * - Settings page (SalesforceFieldMappings.tsx) for team-level CRM field mapping
 * - Export UX for per-export field selection
 */
export const SALESFORCE_CONTACT_FIELDS: CrmMappableField[] = [
  { source: 'id', label: 'Edulead ID', crmType: 'string', required: false, creatable: true },
  { source: 'email', label: 'Email', crmType: 'email', required: true, creatable: true },
  { source: 'firstName', label: 'First Name', crmType: 'string', required: false, creatable: true },
  { source: 'lastName', label: 'Last Name', crmType: 'string', required: true, creatable: true },
  { source: 'title', label: 'Job Title', crmType: 'string', required: false, creatable: true },
  { source: 'titleModifier', label: 'Title Modifier', crmType: 'string', required: false, creatable: true },
  { source: 'titleArea', label: 'Title Area', crmType: 'string', required: false, creatable: true },
  { source: 'titleRole', label: 'Title Role', crmType: 'string', required: false, creatable: true },
  { source: 'decisionLevel', label: 'Decision Level', crmType: 'string', required: false, creatable: true },
  { source: 'functionalGroup', label: 'Functional Group', crmType: 'string', required: false, creatable: true },
  { source: 'phone', label: 'Phone', crmType: 'phone', required: false, creatable: true },
  { source: 'emailDomain', label: 'Email Domain', crmType: 'string', required: false, creatable: true },
  { source: 'emailVerificationStatus', label: 'Email Verification Status', crmType: 'string', required: false, creatable: true },
  { source: 'state', label: 'State', crmType: 'string', required: false, creatable: true },
  { source: 'importDate', label: 'Import Date', crmType: 'datetime', required: false, creatable: true },
];

/**
 * All fields that can be mapped for ACCOUNT/INSTITUTION exports to Salesforce.
 * Used by:
 * - Settings page (SalesforceFieldMappings.tsx) for team-level CRM field mapping
 * - Export UX for per-export field selection
 */
export const SALESFORCE_ACCOUNT_FIELDS: CrmMappableField[] = [
  { source: 'ncesIdDistrict', label: 'District NCES ID', crmType: 'string', required: true, creatable: true },
  { source: 'ncesIdSchool', label: 'School NCES ID', crmType: 'string', required: true, creatable: true },
  { source: 'eduleadId', label: 'Edulead ID', crmType: 'string', required: true, creatable: true },
  { source: 'name', label: 'Name', crmType: 'string', required: true, creatable: true },
  { source: 'type', label: 'Type', crmType: 'string', required: false, creatable: true },
  { source: 'subtype', label: 'Subtype', crmType: 'string', required: false, creatable: true },
  { source: 'model', label: 'Model', crmType: 'string', required: false, creatable: true },
  { source: 'religiousAffiliation', label: 'Religious Affiliation', crmType: 'string', required: false, creatable: true },
  { source: 'access', label: 'Access', crmType: 'string', required: false, creatable: true },
  { source: 'annualRevenue', label: 'Annual Revenue', crmType: 'number', required: false, creatable: true },
  { source: 'nrChildInstitutions', label: 'Number of Child Institutions', crmType: 'number', required: false, creatable: true },
  { source: 'nrStudents', label: 'Number of Students', crmType: 'number', required: false, creatable: true },
  { source: 'address', label: 'Address', crmType: 'string', required: false, creatable: true },
  { source: 'street', label: 'Street', crmType: 'string', required: false, creatable: true },
  { source: 'city', label: 'City', crmType: 'string', required: false, creatable: true },
  { source: 'state', label: 'State', crmType: 'string', required: false, creatable: true },
  { source: 'zip', label: 'ZIP', crmType: 'string', required: false, creatable: true },
  { source: 'country', label: 'Country', crmType: 'string', required: false, creatable: true },
  { source: 'latitude', label: 'Latitude', crmType: 'number', required: false, creatable: true },
  { source: 'longitude', label: 'Longitude', crmType: 'number', required: false, creatable: true },
  { source: 'phone', label: 'Phone', crmType: 'phone', required: false, creatable: true },
  { source: 'fax', label: 'Fax', crmType: 'phone', required: false, creatable: true },
  { source: 'website', label: 'Website', crmType: 'url', required: false, creatable: true },
  { source: 'titleOne', label: 'Title I', crmType: 'bool', required: false, creatable: true },
];

/**
 * Get all required contact field names for Salesforce exports
 */
const getSalesforceRequiredContactFields = (): string[] =>
  SALESFORCE_CONTACT_FIELDS.filter(f => f.required).map(f => f.source);

/**
 * Get all required account field names for Salesforce exports
 */
const getSalesforceRequiredAccountFields = (): string[] =>
  SALESFORCE_ACCOUNT_FIELDS.filter(f => f.required).map(f => f.source);

// Re-export common types;
